import { useState, useEffect } from 'react';
import { Box, Container, Stack, Typography, useTheme } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';

import useTable from 'hooks/useTable';
import MuiDataGrid from 'components/MuiDataGrid';
import { FormProps, IEtcImages } from '../PatientList/api/type';
import ListActionButtons from './ListActionButtons';
import EtcDialog from './EtcDialog';
import useNotification from 'hooks/useNotification';
import { updateMiscHidden } from 'apis/admin';

interface Props extends FormProps {
  defaultList?: IEtcImages[];
}

const EtcList = (props: Props) => {
  const { setValue, defaultList } = props;
  const { palette } = useTheme();
  const { onSuccess, onFail } = useNotification();
  const { list, setList } = useTable<IEtcImages>();
  const [defaultValue, setDefaultValue] = useState<Partial<IEtcImages> | null>(
    null
  );

  useEffect(
    () =>
      setList(
        defaultList?.map((item: any) => {
          const miscellaneousImages = Array.from(
            { length: 5 },
            (_, i) => item[`miscellaneousImage${i + 1}`]
          ).filter(item => item);
          return {
            ...item,
            miscellaneousImages,
          };
        }) ?? []
      ),
    [defaultList, setList]
  );
  useEffect(() => {
    setValue(
      'miscellaneous',
      list.map(item => {
        // Rebuild image files into keys matching the API
        const images = item.miscellaneousImages?.reduce((prev, next, index) => {
          if (!next) return prev;
          return { ...prev, [`miscellaneousImage${index + 1}`]: next };
        }, {});

        // Store object in API update format
        return {
          ...item,
          ...images,
        };
      })
    );
  }, [list, setValue]);

  const onCreateList = (data: IEtcImages) => setList(list.concat(data));
  const onUpdateList = (data: IEtcImages) =>
    setList(
      list.map(item =>
        item.ptMiscellaneousNo === data.ptMiscellaneousNo ? data : item
      )
    );

  const ActionButtons = ({ row }: { row: IEtcImages }) => {
    const onUpdate = () => setDefaultValue(row);

    const onToggleHidden = async () => {
      const nextHidden = !row.isHidden;

      try {
        await updateMiscHidden(row.ptMiscellaneousUuid);

        setList(prev =>
          prev.map(item =>
            item.ptMiscellaneousNo === row.ptMiscellaneousNo
              ? { ...item, isHidden: nextHidden }
              : item
          )
        );

        onSuccess(
          nextHidden ? 'Item has been hidden.' : 'Item is now visible.'
        );
      } catch (error) {
        console.error('Error occurred while toggling visibility:', error);
        onFail(
          nextHidden
            ? 'Failed to hide the item.'
            : 'Failed to make the item visible.',
          error
        );
      }
    };

    const onDelete = () =>
      setList(prev =>
        prev.filter(item => item.ptMiscellaneousNo !== row.ptMiscellaneousNo)
      );

    return (
      <ListActionButtons
        onUpdate={onUpdate}
        onDelete={onDelete}
        onToggleHidden={onToggleHidden}
        isHidden={row.isHidden}
      />
    );
  };

  const columns: GridColDef[] = [
    { field: 'ptMiscellaneousNo', headerName: 'Record No.' },
    { field: 'ptMiscellaneousItem', headerName: 'Record Name' },
    // { field: 'feeKor', headerName: 'Test Name (Korean)' },
    {
      align: 'right',
      field: 'actions',
      headerName: '',
      renderCell: ({ row }) => <ActionButtons row={row} />,
    },
  ];

  return (
    <Container
      maxWidth={false}
      disableGutters
      component="section"
      sx={{ background: '#fff', p: 2 }}
    >
      <Stack spacing={2}>
        <Box display="flex" alignItems="center">
          <Typography variant="h5" sx={{ color: `${palette.primary.main}` }}>
            Miscellaneous Records (Editable)
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 400,
              color: '#555555a4',
              marginLeft: '30px',
            }}
          >
            (For additions, please contact SmartNurse ENR.)
          </Typography>
          {/* <Button
            size="small"
            variant="outlined"
            onClick={onCreateStart}
            sx={{ ml: 3 }}
          >
            Add
          </Button> */}
        </Box>

        <MuiDataGrid
          columns={columns}
          rows={list.map(item => ({ ...item, id: item.ptMiscellaneousNo }))}
        />
      </Stack>

      {defaultValue && (
        <EtcDialog
          isUpdate={!!defaultValue.ptMiscellaneousNo}
          defaultValue={defaultValue}
          onClose={() => setDefaultValue(null)}
          onSearchNo={(no: number) =>
            list.find(item => item.ptMiscellaneousNo === no) ? true : false
          }
          onSave={defaultValue.ptMiscellaneousNo ? onUpdateList : onCreateList}
        />
      )}
    </Container>
  );
};

export default EtcList;
